import { formatDate } from '../../../utils/dateUtils';

/**
 * Service for formatting and processing closeout docs history data
 *
 * This service provides utility functions for:
 * - Formatting change types (FOLDER_CREATED -> "Folder Created")
 * - Formatting field names and values for display
 * - Formatting timestamps (relative and absolute)
 * - Color mapping for different action types
 * - File size formatting
 */

/**
 * Format change type for display
 * @param {string} changeType - The raw change type from API
 * @returns {string} User-friendly change type
 */
export const formatChangeType = (changeType) => {
    const changeTypeMap = {
        FOLDER_CREATED: 'Folder Created',
        FOLDER_UPDATED: 'Folder Updated',
        FOLDER_DELETED: 'Folder Deleted',
        FOLDER_MOVED: 'Folder Moved',
        FOLDER_RENAMED: 'Folder Renamed',
        DOCUMENT_UPLOADED: 'Document Uploaded',
        DOCUMENT_UPDATED: 'Document Updated',
        DOCUMENT_DELETED: 'Document Deleted',
        DOCUMENT_MOVED: 'Document Moved',
    };

    return changeTypeMap[changeType] || changeType;
};

/**
 * Get color for change type chip
 * @param {string} changeType - The change type
 * @returns {string} MUI color
 */
export const getChangeTypeColor = (changeType) => {
    const colorMap = {
        FOLDER_CREATED: 'success',
        DOCUMENT_UPLOADED: 'success',
        FOLDER_UPDATED: 'primary',
        DOCUMENT_UPDATED: 'primary',
        FOLDER_MOVED: 'info',
        DOCUMENT_MOVED: 'info',
        FOLDER_RENAMED: 'secondary',
        FOLDER_DELETED: 'error',
        DOCUMENT_DELETED: 'error',
    };
    return colorMap[changeType] || 'default';
};

/**
 * Get icon for change type
 * @param {string} changeType - The change type
 * @returns {string} Icon name (for MUI icons)
 */
export const getChangeTypeIcon = (changeType) => {
    const iconMap = {
        FOLDER_CREATED: 'CreateNewFolder',
        DOCUMENT_UPLOADED: 'UploadFile',
        FOLDER_UPDATED: 'Edit',
        DOCUMENT_UPDATED: 'Edit',
        FOLDER_MOVED: 'DriveFileMove',
        DOCUMENT_MOVED: 'DriveFileMove',
        FOLDER_RENAMED: 'DriveFileRenameOutline',
        FOLDER_DELETED: 'DeleteOutline',
        DOCUMENT_DELETED: 'DeleteOutline',
    };
    return iconMap[changeType] || 'Info';
};

/**
 * Format field names for display
 * @param {string} fieldName - The raw field name from API
 * @returns {string} User-friendly field name
 */
export const formatFieldName = (fieldName) => {
    const fieldNameMap = {
        folderName: 'Folder Name',
        parentFolderId: 'Location',
        parentFolder: 'Location',
        location: 'Location',
        originalFilename: 'File Name',
        filename: 'File Name',
        folder: 'Location',
        folderId: 'Location',
        deletedAt: 'Deleted',
        deletedBy: 'Deleted By',
        lastModifiedAt: 'Last Modified',
        mimeType: 'File Type',
        fileSizeBytes: 'File Size',
    };

    return fieldNameMap[fieldName] || fieldName;
};

/**
 * Format field values for display
 * @param {string} fieldName - The field name
 * @param {any} value - The field value
 * @returns {string} Formatted field value
 */
export const formatFieldValue = (fieldName, value) => {
    if (value === null || value === undefined) return 'None';
    if (value === '') return 'Empty';

    // Format file size
    if (fieldName === 'fileSizeBytes' && typeof value === 'number') {
        return formatFileSize(value);
    }

    // Format dates
    if (fieldName.includes('At') && value) {
        return formatDate(value);
    }

    return value.toString();
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format timestamp for relative display (e.g., "2 hours ago")
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
        return 'Just now';
    } else if (diffMin < 60) {
        return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHour < 24) {
        return `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDay < 7) {
        return `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
    } else {
        return formatDate(timestamp);
    }
};

/**
 * Format absolute timestamp for tooltip
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted date and time
 */
export const formatAbsoluteTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};
